/**
 * Línea de intro del Programa guiado (2026-10-04).
 *
 * Al entrar a una práctica guiada SIN pasar por la pantalla del Programa (el
 * CTA "Practicá X" del cierre, el radar, el próximo paso) el user no vio la
 * tarjeta de la unidad, el tracker de objetivos arranca plegado y el coach
 * tiene prohibido anunciar la lección. Caso real: el saludo pedía "something
 * you've never tried" y el user no sabía qué se esperaba de él. Esta línea,
 * arriba del hilo, dice qué se practica y dónde están los objetivos.
 *
 * Sale del `subunit.advanced` inicial (título + `blurbEs` del currículum, que
 * ya trae un ejemplo). Sin sub-unidad autorada o sin `blurbEs` no hay intro:
 * mejor nada que una línea genérica.
 */
import { useState } from 'react';
import type { ActiveSubUnitClient } from './hooks/useObjectiveTrackers.js';

export interface ProgramIntro {
  title: string;
  blurb: string;
  objectiveCount: number;
}

export function buildProgramIntro(args: {
  /** La entrada no pasó por la pantalla del Programa y no es una charla retomada. */
  eligible: boolean;
  subUnit: Pick<ActiveSubUnitClient, 'titleEs' | 'blurbEs' | 'objectives'> | null;
}): ProgramIntro | null {
  const { eligible, subUnit } = args;
  if (!eligible || !subUnit?.blurbEs || subUnit.titleEs.length === 0) return null;
  return {
    title: subUnit.titleEs,
    blurb: subUnit.blurbEs,
    objectiveCount: subUnit.objectives.length,
  };
}

/**
 * La intro del render en curso, FIJADA en la primera que se pudo armar: es el
 * encabezado de cómo abrió la charla. Si el server avanza de sub-unidad a mitad
 * de sesión, el tracker cambia (es "lo que practicás ahora"), pero el
 * encabezado arriba de mensajes viejos no debe reescribirse solo.
 * Tras un F5 la sesión se re-engancha con el mismo `location.state` y la intro
 * vuelve a armarse: es la misma charla, así que el encabezado sigue siendo
 * correcto arriba del hilo re-sembrado.
 */
export function useProgramIntro(
  eligible: boolean,
  subUnit: Pick<ActiveSubUnitClient, 'titleEs' | 'blurbEs' | 'objectives'> | null,
): ProgramIntro | null {
  const [pinned, setPinned] = useState<ProgramIntro | null>(null);
  if (pinned !== null) return pinned;
  const candidate = buildProgramIntro({ eligible, subUnit });
  // Ajuste durante el render (patrón derive-from-previous): fija sin un render
  // de más con el valor viejo.
  if (candidate !== null) setPinned(candidate);
  return candidate;
}
